import { debounce } from "@/src/helpers/utils";
import { plumeAPI } from "@/src/helpers/ServiceHelper";
import {
  updateChapterInIndexedDb,
  createBookInIndexedDb,
  deleteChapterFromIndexedDb,
  getBooksFromIndexedDB,
  saveBooksToIndexedDb,
  getSingleBookFromIndexedDB,
  updateChaptersInIndexedDb,
} from "@/src/helpers/indexedDB";
import { Book, Chapter, convertToChapterObject } from "@/src/models/BooksModel.js";
import { convertToBookObject } from "@/src/models/BooksModel.js";
import axiosRetry from "axios-retry";
import { captureEvent } from "@/src/helpers/trackEvent";
import { ROLES } from "@/src/lib/constants";
import { hydratePreferences } from "@/src/lib/preferences";
import { logoutOnAuthError } from "@/src/helpers/authErrors";
import { canonicalTier, orderedTiers } from "@/src/helpers/tiers";

// Keeps `ids=...` comfortably under gunicorn's 4094-byte request-line limit --
// ~530 comma-joined comment IDs was enough to trip it in prod.
const MAX_COMMENT_IDS_PER_REQUEST = 300;

export function getMultipleComments({ getAccessTokenSilently, logout, getCommentIds }) {
  return getAccessTokenSilently()
    .then(async (token) => {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const batches = [];
      for (let i = 0; i < getCommentIds.length; i += MAX_COMMENT_IDS_PER_REQUEST) {
        batches.push(getCommentIds.slice(i, i + MAX_COMMENT_IDS_PER_REQUEST));
      }

      const batchResults = await Promise.all(
        batches.map((batchIds) => {
          const queryString = `ids=${batchIds.join(",")}`;
          const urlWithParams = `critique_api/comments?${queryString}`;

          return plumeAPI
            .get(urlWithParams, config)
            .then((response) => {
              if (response.status !== 200) {
                throw new Error(`Error fetching comments: ${response.statusText}`);
              }
              return response.data; // Assuming the API returns the list of comments in response.data
            })
            .catch((error) => {
              console.error("Error fetching multiple comments:", error);
              return [];
            });
        }),
      );

      return batchResults.flat();
    })
    .catch((error) => {
      console.error("Could not authenticate", error);
      logoutOnAuthError(error, logout);
    });
}

// Comment outcomes must survive the page going away. Applying a rewrite or
// dismissing a note is very often the last thing a user does before closing the
// tab or navigating, and an in-flight XHR is CANCELLED on unload — which is how
// accepts were getting lost. `fetch(keepalive)` is the one request the browser
// promises to finish regardless. Body is "{}", far under the 64KB keepalive cap.
//
// Deliberately not awaited by callers: the editor mark is removed either way,
// and the write must never block the interaction.
async function persistCommentOutcome({ getAccessTokenSilently, logout, method, path, op }) {
  try {
    const token = await getAccessTokenSilently();
    const base = (plumeAPI.defaults.baseURL || "").replace(/\/$/, "");
    const res = await fetch(`${base}/${path.replace(/^\//, "")}`, {
      method,
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: "{}",
      keepalive: true,
    });
    if (!res.ok) {
      captureEvent("comment_write_failed", { op, status: res.status });
      console.error(`${op} failed`, res.status);
    }
  } catch (error) {
    captureEvent("comment_write_failed", { op, status: error?.response?.status });
    console.error(`${op} failed`, error);
    logoutOnAuthError(error, logout);
  }
}

export function resolveComment({ getAccessTokenSilently, logout, commentId }) {
  return persistCommentOutcome({
    getAccessTokenSilently,
    logout,
    method: "PUT",
    op: "resolve",
    path: `critique_api/comments/resolve/${String(commentId).replace("comment", "")}`,
  });
}

// Clearing a whole rail fires many outcome writes at once, and a clear is
// usually followed immediately by navigation or a refresh. One token fetch for
// the batch, then every keepalive request leaves in the same tick — so an
// unload can cut off at most the token fetch, never the Nth write queued
// behind the (N-1)th token round-trip. That is how one resolve out of
// fourteen went missing and left a phantom row no editor could ever clear.
export async function resolveCommentsBatch({ getAccessTokenSilently, logout, commentIds }) {
  let token;
  try {
    token = await getAccessTokenSilently();
  } catch (error) {
    console.error("Could not authenticate", error);
    logoutOnAuthError(error, logout);
    return;
  }
  const base = (plumeAPI.defaults.baseURL || "").replace(/\/$/, "");
  for (const id of commentIds) {
    const bare = String(id).replace("comment", "");
    fetch(`${base}/critique_api/comments/resolve/${bare}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: "{}",
      keepalive: true,
    })
      .then((res) => {
        if (!res.ok) {
          captureEvent("comment_write_failed", { op: "resolve", status: res.status });
          console.error("resolve failed", res.status);
        }
      })
      .catch((error) => {
        captureEvent("comment_write_failed", { op: "resolve" });
        console.error("resolve failed", error);
      });
  }
}

export function getConversationMessages({ getAccessTokenSilently, logout, conversationId, plumeAPI, setConversations, limit, beforeId }) {
  const toArray = (value) => (Array.isArray(value) ? value : []);

  // Rows come back from the v2 endpoint already normalized:
  // { kind, message_id, content, parent_id, tool_calls?, tool_call_id?, ... }
  // Both legacy `Chats` rows and new `agent_messages` rows share this shape.

  const mergePaginated = (incomingOlder, existingNewer) => {
    const byId = new Map();
    for (const r of incomingOlder) byId.set(r.message_id, r);
    for (const r of existingNewer) if (!byId.has(r.message_id)) byId.set(r.message_id, r);
    return Array.from(byId.values()).sort((a, b) => a.message_id - b.message_id);
  };

  const mergePreserveOptimistic = (incoming, existing) => {
    // Optimistic rows always have negative message_ids; keep them so a fetch
    // racing with a just-sent message doesn't blank the user's input.
    const optimistic = existing.filter((r) => typeof r?.message_id === "number" && r.message_id < 0);
    if (incoming.length === 0 && existing.length > 0) return existing;
    if (optimistic.length > 0) {
      const incomingIds = new Set(incoming.map((r) => r.message_id));
      return [...incoming, ...optimistic.filter((r) => !incomingIds.has(r.message_id))];
    }
    return incoming;
  };

  return getAccessTokenSilently()
    .then((token) => {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const params = new URLSearchParams();
      if (typeof limit === "number") params.set("limit", String(limit));
      if (typeof beforeId === "number") params.set("before_id", String(beforeId));
      const qs = params.toString();
      const url = `/critique_api/chats/messages/${conversationId}${qs ? `?${qs}` : ""}`;

      return plumeAPI
        .get(url, config)
        .then(function (response) {
          const data = response["data"];
          setConversations((prevConversations) =>
            prevConversations.map((conv) => {
              if (conv.conversation_id !== conversationId) return conv;

              const existing = toArray(conv.chats);
              const incoming = toArray(data?.messages);
              const merged =
                typeof beforeId === "number" ? mergePaginated(incoming, existing) : mergePreserveOptimistic(incoming, existing);

              return {
                ...conv,
                chats: merged,
                chats_has_more: !!data.has_more,
                chats_next_before_id: data.next_before_id ?? null,
                chats_loaded: true,
                leaf_message_id: data.leaf_message_id ?? conv.leaf_message_id ?? null,
              };
            }),
          );
          return data;
        })
        .catch(function (error) {
          console.error("Error fetching conversation messages:", error);
          throw error;
        });
    })
    .catch((error) => {
      console.error("Could not authenticate", error);
      logoutOnAuthError(error, logout);
      throw error;
    });
}

export function getChat({ getAccessTokenSilently, logout, bookId, plumeAPI, setConversations, setIsChatLoading, setActiveConversation }) {
  if (bookId) {
    getAccessTokenSilently()
      .then((token) => {
        const config = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        };

        plumeAPI
          .get(`/critique_api/chats/${bookId}/list`, config)
          .then(function (response) {
            setIsChatLoading(false);
            const conversations = response["data"];
            setConversations(conversations);

            // If there are conversations, set the first one (most recent) as active
            if (conversations.length > 0) {
              const recentConversationId = conversations[0].conversation_id;
              setActiveConversation(recentConversationId);
            }
          })
          .catch(function (error) {
            setIsChatLoading(false);
            console.error("Error fetching conversations list:", error);
          });
      })
      .catch((error) => {
        console.error("Could not authenticate", error);
        logoutOnAuthError(error, logout);
        throw error;
      });
  }
}

export function startNewConversation({ getAccessTokenSilently, logout, bookId, plumeAPI, setConversations, setActiveConversation }) {
  return getAccessTokenSilently()
    .then((token) => {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };
      return plumeAPI
        .post("/critique_api/chats/" + bookId, {}, config)
        .then((r) => {
          const newConversationId = r["data"]["conversation_id"];
          setConversations((prevConversations) => [
            ...prevConversations,
            {
              conversation_id: newConversationId,
              title: "New Chat",
              created_at: r["data"]["created_at"],
              updated_at: r["data"]["updated_at"],
              book_id: bookId,
              chats: [],
            },
          ]);
          setActiveConversation(newConversationId);
          return newConversationId;
        })
        .catch((e) => {
          // Returning null silently is what made "new chat won't open"
          // invisible. Covers failures after the token is in hand; an auth
          // failure rejects earlier and is handled by the outer catch.
          // Status only, never the error: an AxiosError carries `config.headers`,
          // so serializing it would ship the bearer token.
          captureEvent("conversation_create_failed", { status: e?.response?.status ?? null, book_id: bookId });
          console.error("Could not start a new conversation. status:", e?.response?.status ?? "network error");
          return null;
        });
    })
    .catch((error) => {
      console.error("Could not authenticate", error);
      logoutOnAuthError(error, logout);
      return null;
    });
}

export function createBook({ getAccessTokenSilently, logout, bookId, title, plumeAPI, editorDispatch }) {
  return new Promise((resolve, reject) => {
    if (bookId === 0) {
      getAccessTokenSilently()
        .then((token) => {
          const config = {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          };
          editorDispatch({ type: "saving/set", payload: true });
          return plumeAPI.post(
            "/books_api/books",
            {
              title: title.length === 0 ? "Untitled Draft" : title,
            },
            config,
          );
        })
        .then(function (response) {
          captureEvent("book_created");
          editorDispatch({ type: "bookId/set", payload: response.data["book"]["id"] });
          editorDispatch({ type: "chapters/set", payload: [response.data["chapter"]] });
          editorDispatch({ type: "role/set", payload: response.data["book"]["role"] });
          editorDispatch({ type: "saving/set", payload: false });

          let currentDate = new Date().toISOString();

          let chapterData = {
            book_id: response.data["book"]["id"],
            chapter_number: 1,
            content: "",
            created_at: currentDate,
            id: response.data["chapter"]["id"],
            title: "Chapter 1",
            word_count_trigger: response.data["chapter"]["word_count_trigger"],
          };

          let bookData = {
            id: response.data["book"]["id"],
            time_posted: currentDate,
            time_updated: currentDate,
            role: response.data["book"]["role"],
            title: "Untitled Draft",
            chapters: [chapterData],
            target_word_count: response.data["book"]["target_word_count"],
          };

          let newBook = new Book({ book: bookData });

          createBookInIndexedDb(newBook);
          editorDispatch({ type: "books/add", payload: newBook });
          editorDispatch({
            type: "booksMetadata/add",
            payload: {
              id: response.data["book"]["id"],
              time_posted: currentDate,
              time_updated: currentDate,
              title: bookData.title,
              target_word_count: response.data["book"]["target_word_count"],
              is_public: false,
              role: response.data["book"]["role"],
              chapter_count: 1,
              chapter_ids: [response.data["chapter"]["id"]],
              preview: "",
              total_characters: 0,
            },
          });
          resolve(response.data);
        })
        .catch(function (error) {
          editorDispatch({ type: "saving/set", payload: false });
          console.error("Error creating book:", error);
          reject(error);
        });
    } else {
      resolve(null);
    }
  });
}

export async function saveChapters({ getAccessTokenSilently, logout, chapters, setLastSave, plumeAPI }) {
  const transformedChapters = chapters.map((chapter) => ({
    chapter_id: chapter.id,
    content: chapter.content,
    title: chapter.title,
  }));

  let token;
  try {
    token = await getAccessTokenSilently();
  } catch (error) {
    console.error("Could not authenticate", error);
    logoutOnAuthError(error, logout);
    throw error; // Rethrow to be caught by the calling hook
  }

  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await plumeAPI.put(`/books_api/chapters/update_all`, transformedChapters, config);
    setLastSave(new Date().getTime());
    return response;
  } catch (error) {
    // This is an API error (e.g., 400, 500), not an auth error.
    // Just rethrow for the calling hook to handle without logging out.
    throw error;
  }
}

// Save the chapters of a reviewed import in one request; the server numbers
// them from the book's current MAX. Resolves to Chapter objects.
export async function commitImportedChapters({ getAccessTokenSilently, bookId, chapters }) {
  const token = await getAccessTokenSilently();
  const config = { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } };
  const body = chapters.map(({ title, content }) => ({ title, content }));
  const response = await plumeAPI.post(`/books_api/books/${bookId}/chapters/bulk`, body, config);
  captureEvent("chapters_imported", { count: body.length });
  return response.data.map(convertToChapterObject);
}

export function addChapter({ getAccessTokenSilently, logout, bookId, plumeAPI, editorDispatch, chapters }) {
  return getAccessTokenSilently()
    .then((token) => {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };
      // Return the promise from `plumeAPI.post`
      return plumeAPI.post(`/books_api/books/${bookId}/chapters`, {}, config);
    })
    .then((response) => {
      captureEvent("chapter_added");
      const updatedChapters = [...chapters, convertToChapterObject(response.data)];
      editorDispatch({ type: "chapters/set", payload: updatedChapters });
      updateChapterInIndexedDb(updatedChapters, bookId);
      return response.data; // This value will be returned to the outer promise
    })
    .catch((error) => {
      console.error("Could not authenticate or post the chapter:", error);
      logoutOnAuthError(error, logout);
      // If we want the caller to see the error, we can rethrow
      throw error;
    });
}

function regularPutTitle({ getAccessTokenSilently, logout, bookId, body, plumeAPI, editorDispatch }) {
  getAccessTokenSilently()
    .then((token) => {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };
      plumeAPI
        .put("/books_api/books/" + bookId, body, config)
        .then(function (response) {
          editorDispatch({ type: "bookId/set", payload: response.data["id"] });
        })
        .catch(function (error) {});
    })
    .catch((error) => {
      console.error("Could not authenticate", error);
      logoutOnAuthError(error, logout);
    });
}

export const putTitle = debounce(regularPutTitle, 1000);

export function thumbsUp({ getAccessTokenSilently, logout, strippedId }) {
  return persistCommentOutcome({
    getAccessTokenSilently,
    logout,
    method: "PATCH",
    op: "thumbsup",
    path: `critique_api/comments/thumbsup/${strippedId}`,
  });
}

export function thumbsDown({ getAccessTokenSilently, logout, strippedId }) {
  return persistCommentOutcome({
    getAccessTokenSilently,
    logout,
    method: "PATCH",
    op: "thumbsdown",
    path: `critique_api/comments/thumbsdown/${strippedId}`,
  });
}

export function deleteChapters({ getAccessTokenSilently, logout, chapterId, plumeAPI }) {
  captureEvent("chapter_deleted");
  deleteChapterFromIndexedDb(chapterId);
  return getAccessTokenSilently()
    .then((token) => {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };
      return plumeAPI.delete("/books_api/chapters/" + chapterId, config);
    })
    .catch((error) => {
      console.error("Could not authenticate", error);
      logoutOnAuthError(error, logout);
    });
}

function regularUpdateChapterTitle({ getAccessTokenSilently, logout, chapterId, title, plumeAPI }) {
  getAccessTokenSilently()
    .then((token) => {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };
      const body = {
        title: title,
      };
      plumeAPI
        .patch("/books_api/chapters/" + chapterId, body, config)
        .then(function (response) {
          // Handle the response here
        })
        .catch(function (error) {
          console.error(error);
        });
    })
    .catch((error) => {
      console.error("Could not authenticate", error);
      logoutOnAuthError(error, logout);
    });
}

export const updateChapterTitle = debounce(regularUpdateChapterTitle, 1000);

// A partial preferences map; the server merges it and returns the whole map.
export async function updateUserSettings({ getAccessTokenSilently, patch }) {
  const token = await getAccessTokenSilently();
  const config = { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } };
  const response = await plumeAPI.patch("/users_api/users/settings", patch, config);
  return response.data;
}

export function setUserAcceptedTerms({ getAccessTokenSilently, logout, plumeAPI }) {
  return getAccessTokenSilently()
    .then((token) => {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      return plumeAPI
        .put("/users_api/users/accepted_terms", {}, config)
        .then(function (response) {
          return response;
        })
        .catch(function (error) {
          console.error(error);
        });
    })
    .catch((error) => {
      console.error("Could not authenticate", error);
      logoutOnAuthError(error, logout);
    });
}

export async function fetchBooksMetadataFromDb({ getAccessTokenSilently, isAuthenticated, logout, plumeAPI, editorDispatch }) {
  if (!isAuthenticated) {
    editorDispatch({ type: "studio/loading/set", payload: false });
    return null;
  }

  const reloadCount = parseInt(sessionStorage.getItem("reloadCount") || "0");
  const timeoutDuration = 20000 + reloadCount * 10000;

  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      editorDispatch({ type: "studio/error/set", payload: "default" });
      reject(new Error("Request timed out"));
    }, timeoutDuration);
  });

  const fetchPromise = (async () => {
    editorDispatch({ type: "studio/loading/set", payload: true });
    editorDispatch({ type: "studio/ready/set", payload: false });
    editorDispatch({ type: "studio/error/set", payload: null });

    let token;
    try {
      token = await getAccessTokenSilently();
    } catch (e) {
      console.error("Could not authenticate", e);
      logoutOnAuthError(e, logout);
      throw e;
    }

    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      timeout: timeoutDuration,
    };

    const response = await plumeAPI.get("/users_api/users/fetch_one", config);
    document.querySelector(".loading-gif")?.classList.add("explode");

    editorDispatch({ type: "openExpressionAllowed/set", payload: response.data.accepted_terms });
    hydratePreferences(response.data.settings);

    if (response.data?.usage_summary) {
      editorDispatch({ type: "usageSummary/set", payload: response.data.usage_summary });
    }

    const booksMetadata = (response.data?.books_metadata || []).sort((a, b) => new Date(b.time_updated) - new Date(a.time_updated));
    editorDispatch({ type: "booksMetadata/set", payload: booksMetadata });
    return booksMetadata;
  })();

  return Promise.race([fetchPromise, timeoutPromise])
    .then((booksMetadata) => {
      editorDispatch({ type: "studio/loading/set", payload: false });
      editorDispatch({ type: "studio/ready/set", payload: true });
      clearTimeout(timeoutId);
      sessionStorage.setItem("reloadCount", "0");
      return booksMetadata;
    })
    .catch((error) => {
      console.error("Fetch failed or timed out", error);
      editorDispatch({ type: "studio/loading/set", payload: false });
      if (error?.message === "Request timed out") {
        sessionStorage.setItem("reloadCount", (reloadCount + 1).toString());
        if (reloadCount < 2) {
          window.location.reload();
        } else {
          editorDispatch({ type: "studio/error/set", payload: "repeated" });
        }
      } else {
        editorDispatch({ type: "studio/error/set", payload: "default" });
      }
      clearTimeout(timeoutId);
    });
}

export async function fetchBooksByIds({ getAccessTokenSilently, logout, ids, plumeAPI, signal }) {
  if (!Array.isArray(ids) || ids.length === 0) return { books: [] };

  let token;
  try {
    token = await getAccessTokenSilently();
  } catch (e) {
    console.error("Could not authenticate", e);
    logoutOnAuthError(e, logout);
    throw e;
  }

  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };

  const resp = await plumeAPI.post("/books_api/books/fetch_many", { ids }, { ...config, signal });
  return resp.data;
}

// Check if a book is public
export function checkIfBookIsPublic(bookId) {
  // Checking if book is public
  return plumeAPI
    .get(`/books_api/books/${bookId}/public-status`)
    .then((response) => {
      // Public status response received
      return response.data.is_public;
    })
    .catch((error) => {
      console.error("Error checking book public status:", error);
      console.error("Error details:", error.response?.data || error.message);
      return false;
    });
}

// Fetch public book data
export function fetchPublicBook(bookId) {
  // Fetching public book
  return plumeAPI
    .get(`/books_api/books/${bookId}/public`)
    .then((response) => {
      // Public book data received
      // The API returns the book data directly, not nested under 'book'
      return response.data;
    })
    .catch((error) => {
      console.error("Error fetching public book:", error);
      console.error("Error details:", error.response?.data || error.message);
      throw error;
    });
}

// Update book visibility (for the missing function in VisibilityToggle)
export function updateBookVisibility({ getAccessTokenSilently, logout, bookId, is_public, plumeAPI }) {
  return getAccessTokenSilently()
    .then((token) => {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };
      const body = {
        is_public: is_public,
      };
      return plumeAPI
        .put(`/books_api/books/${bookId}/visibility`, body, config)
        .then((response) => {
          return response.data;
        })
        .catch((error) => {
          console.error("Error updating book visibility:", error);
          throw error;
        });
    })
    .catch((error) => {
      console.error("Could not authenticate", error);
      logoutOnAuthError(error, logout);
      throw error;
    });
}

// Helper function to set up editor context with book data
function setupEditorContext(editorDispatch, bookData, role, isPublic = false) {
  if (role === ROLES.PUBLIC_VIEWER) {
    captureEvent("public_book_viewed", { book_id: bookData.id });
  }
  editorDispatch({ type: "bookId/set", payload: bookData.id });
  editorDispatch({ type: "chapters/set", payload: bookData.chapters });
  editorDispatch({ type: "title/set", payload: bookData.title });
  editorDispatch({ type: "role/set", payload: role });
  editorDispatch({ type: "is_public/set", payload: isPublic });
  editorDispatch({ type: "editor/ready/set", payload: true });
}

// Helper function to handle public book access
async function handlePublicBookAccess(bookId, editorDispatch, setOpenInvalidBookPopup) {
  try {
    const is_public = await checkIfBookIsPublic(bookId);
    if (is_public) {
      const publicBookData = await fetchPublicBook(bookId);
      setupEditorContext(editorDispatch, publicBookData, ROLES.PUBLIC_VIEWER, true);
      return true;
    } else {
      setOpenInvalidBookPopup(true);
      return false;
    }
  } catch (error) {
    console.error("Error checking book public status:", error);
    setOpenInvalidBookPopup(true);
    return false;
  }
}

export async function fetchSingleBookDataFromDb({
  isAuthenticated,
  bookId,
  getAccessTokenSilently,
  logout,
  setOpenInvalidBookPopup,
  setOpenLoginPopup,
  editorDispatch,
}) {
  // fetchSingleBookDataFromDb called
  editorDispatch({ type: "editor/ready/set", payload: false });

  if (isAuthenticated) {
    // Server is the source of truth. Always fetch from the API first so a
    // book edited on another device shows up correctly. IndexedDB is a
    // best-effort offline fallback consulted only when the network itself
    // is unreachable.
    try {
      const token = await getAccessTokenSilently();
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const resp = await plumeAPI.post("/books_api/books/fetch_many", { ids: [bookId] }, config);
      const models = convertToBookObject(resp.data || { books: [] });
      const fetched = models?.[0];

      if (fetched) {
        // Prime the offline cache with the latest server copy. Failure to
        // write the cache shouldn't block the user from opening the book.
        try {
          await saveBooksToIndexedDb([fetched]);
        } catch (cacheErr) {
          console.warn("[fetchSingleBookDataFromDb] Failed to update cache:", cacheErr);
        }
        editorDispatch({ type: "books/add", payload: fetched });
        setupEditorContext(editorDispatch, fetched, fetched.role);

        try {
          const is_public = await checkIfBookIsPublic(bookId);
          editorDispatch({ type: "is_public/set", payload: is_public });
        } catch (error) {
          editorDispatch({ type: "is_public/set", payload: false });
        }
        return;
      }

      // Authenticated but server returned no matching book — user may not
      // have access to a private book. Fall through to public-viewer check.
      await handlePublicBookAccess(bookId, editorDispatch, setOpenInvalidBookPopup);
    } catch (error) {
      // axios sets `error.response` only when the server returned an HTTP
      // response. A missing `response` means the request never made it —
      // genuine offline / DNS / transport error. That's the only case where
      // serving stale cache is safer than failing the load.
      const isNetworkError = !error?.response;
      if (isNetworkError) {
        try {
          const cached = await getSingleBookFromIndexedDB(bookId);
          if (cached) {
            console.warn("[fetchSingleBookDataFromDb] Network error; serving cached copy:", error);
            setupEditorContext(editorDispatch, cached, cached.role);
            return;
          }
        } catch (cacheErr) {
          console.error("[fetchSingleBookDataFromDb] Cache fallback failed:", cacheErr);
        }
      } else {
        console.error("[fetchSingleBookDataFromDb] Server returned an error:", error);
      }
      await handlePublicBookAccess(bookId, editorDispatch, setOpenInvalidBookPopup);
    }
  } else {
    // Unauthenticated user - check if book is public
    return checkIfBookIsPublic(bookId)
      .then((is_public) => {
        if (is_public) {
          // Book is public, fetch it
          return fetchPublicBook(bookId);
        } else {
          // Book is not public, show login popup for unauthenticated users
          editorDispatch({ type: "studio/ready/set", payload: false });
          if (setOpenLoginPopup) {
            setOpenLoginPopup(true);
          } else {
            setOpenInvalidBookPopup(true);
          }
          return null;
        }
      })
      .then((publicBookData) => {
        if (publicBookData) {
          // Setting public book data in editor context
          setupEditorContext(editorDispatch, publicBookData, ROLES.PUBLIC_VIEWER, true);
        }
      })
      .catch((error) => {
        console.error("Error fetching public book:", error);
        editorDispatch({ type: "studio/ready/set", payload: false });
        if (setOpenLoginPopup) {
          setOpenLoginPopup(true);
        } else {
          setOpenInvalidBookPopup(true);
        }
      });
  }
}

export function addUserToBook({ getAccessTokenSilently, plumeAPI, bookId, userEmail }) {
  return getAccessTokenSilently()
    .then((token) => {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };
      const body = {
        user_email: userEmail,
      };
      return plumeAPI
        .post(`/book_permissions_api/books/${bookId}/add_user`, body, config)
        .then((response) => {
          if (response.status === 201) {
            console.log("Successfully added user to book");
          } else if (response.status === 200) {
            console.log("User already has access to this book");
          }
          return response;
        })
        .catch((error) => {
          if (error.response && error.response.status === 404) {
            console.error("Book not found or User email not registered");
          } else if (error.response && error.response.status === 403) {
            console.error("User not authorized");
          } else {
            console.error("Error adding user to book:", error);
          }
          throw error;
        });
    })
    .catch((error) => {
      console.error("Could not authenticate", error);
      throw error;
    });
}

export function getUsersWithAccessToBook({ getAccessTokenSilently, bookId, plumeAPI }) {
  return getAccessTokenSilently()
    .then((token) => {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };
      return plumeAPI
        .get(`/book_permissions_api/books/${bookId}/users`, config)
        .then((response) => {
          return response.data.users;
        })
        .catch((error) => {
          console.error("Error fetching users with access:", error);
          throw error;
        });
    })
    .catch((error) => {
      console.error("Could not authenticate", error);
      throw error;
    });
}

export function editBookPermission({ getAccessTokenSilently, logout, bookId, newPermission, plumeAPI }) {
  return getAccessTokenSilently()
    .then((token) => {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };
      const body = {
        new_permission: newPermission,
      };
      return plumeAPI
        .patch(`/book_permissions_api/books/${bookId}/permissions`, body, config)
        .then((response) => {
          return response;
        })
        .catch((error) => {
          console.error("Error editing book permission:", error);
        });
    })
    .catch((error) => {
      console.error("Could not authenticate", error);
      logoutOnAuthError(error, logout);
      throw error;
    });
}

export function removeUserPermission({ getAccessTokenSilently, bookId, email, plumeAPI }) {
  return getAccessTokenSilently()
    .then((token) => {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };
      return plumeAPI
        .delete(`/book_permissions_api/books/${bookId}/permissions/${email}`, config)
        .then((response) => {
          return response;
        })
        .catch((error) => {
          console.error("Error removing user permission:", error);
        });
    })
    .catch((error) => {
      console.error("Could not authenticate", error);
      throw error;
    });
}

export function updateChapterOrder({ getAccessTokenSilently, logout, bookId, plumeAPI, chapters }) {
  return getAccessTokenSilently()
    .then((token) => {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const body = chapters.map((chapter) => ({
        chapter_id: chapter.id,
        chapter_number: chapter.chapterNumber,
      }));

      return plumeAPI.put(`/books_api/chapters/update_order`, body, config).catch((error) => {
        console.error("Error updating chapter order:", error);
      });
    })
    .catch((error) => {
      console.error("Could not authenticate", error);
      logoutOnAuthError(error, logout);
      throw error;
    });
}

export function getReports({ getAccessTokenSilently, logout, bookId, plumeAPI, setReports }) {
  return getAccessTokenSilently()
    .then((token) => {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      return plumeAPI.get(`/critique_api/reports/${bookId}`, config).then((response) => {
        setReports(response.data);
        return response.data;
      });
    })
    .catch((error) => {
      console.error("Could not authenticate", error);
      logoutOnAuthError(error, logout);
      throw error;
    });
}

export function createUser({ getAccessTokenSilently, plumeAPI, referralCode, referralSource }) {
  return new Promise((resolve, reject) => {
    getAccessTokenSilently()
      .then((token) => {
        const config = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        };

        // Create body with all referral parameters
        const body = {};
        if (referralCode) {
          body.referral_code = referralCode;
        }
        if (referralSource) {
          body.referral_source = referralSource;
        }

        return plumeAPI.post(`/users_api/users`, body, config);
      })
      .then((response) => {
        if (response.data) {
          resolve(response);
        } else {
          reject(new Error("No data received from user creation"));
        }
      })
      .catch((error) => {
        console.error("Error creating user:", error);
        reject(error);
      });
  });
}

export function renameConversation({ getAccessTokenSilently, logout, conversationId, title, plumeAPI, setConversations }) {
  return getAccessTokenSilently()
    .then((token) => {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      return plumeAPI
        .put(`/critique_api/chats/rename/${conversationId}`, { title }, config)
        .then(function (response) {
          // Update conversations locally
          setConversations((prev) => prev.map((conv) => (conv.conversation_id === conversationId ? { ...conv, title } : conv)));
          return response;
        })
        .catch(function (error) {
          console.error("Error renaming conversation:", error);
          throw error;
        });
    })
    .catch((error) => {
      console.error("Could not authenticate", error);
      logoutOnAuthError(error, logout);
      throw error;
    });
}

export function deleteConversation({ getAccessTokenSilently, logout, conversationId, plumeAPI, setConversations, setActiveConversation }) {
  return getAccessTokenSilently()
    .then((token) => {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      return plumeAPI
        .delete(`/critique_api/chats/${conversationId}`, config)
        .then(function (response) {
          // Update conversations locally
          setConversations((prev) => {
            const filtered = prev.filter((conv) => conv.conversation_id !== conversationId);
            // If we've deleted the active conversation, set a new active one
            if (filtered.length > 0) {
              setActiveConversation(filtered[0].conversation_id);
            } else {
              setActiveConversation(null);
            }
            return filtered;
          });
          return response;
        })
        .catch(function (error) {
          console.error("Error deleting conversation:", error);
          throw error;
        });
    })
    .catch((error) => {
      console.error("Could not authenticate", error);
      logoutOnAuthError(error, logout);
      throw error;
    });
}

// Per-lens stats for the review console: all-time totals plus a 14-day
// daily series (see backend comments.compute_review_stats for semantics).
//
// SWR-lite cache: the review panel remounts on every tab flip, but stats only
// really move when a run finishes (callers pass force then) or notes get
// decided (the TTL covers that drift). Concurrent callers share one request;
// a failure never evicts data we already had.
const reviewStatsCache = new Map(); // "bookId:chapterId" -> { data?, fetchedAt?, promise? }
const REVIEW_STATS_TTL_MS = 60_000;

function reviewStatsKey(bookId, chapterId) {
  return `${bookId}:${chapterId ?? ""}`;
}

export function getCachedReviewStats(bookId, chapterId) {
  return reviewStatsCache.get(reviewStatsKey(bookId, chapterId))?.data ?? null;
}

export async function fetchReviewStats({ getAccessTokenSilently, bookId, chapterId, force = false }) {
  const key = reviewStatsKey(bookId, chapterId);
  const cached = reviewStatsCache.get(key);
  if (!force && cached) {
    if (cached.promise) return cached.promise;
    if (Date.now() - cached.fetchedAt < REVIEW_STATS_TTL_MS) return cached.data;
  }
  const promise = (async () => {
    const token = await getAccessTokenSilently();
    const response = await plumeAPI.get(`/critique_api/books/${bookId}/review-stats`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      params: chapterId ? { chapter_id: chapterId } : undefined,
    });
    reviewStatsCache.set(key, { data: response.data, fetchedAt: Date.now() });
    return response.data;
  })();
  reviewStatsCache.set(key, { ...cached, promise });
  try {
    return await promise;
  } catch (error) {
    // Keep stale data if we had any; otherwise clear the failed in-flight.
    if (cached?.data != null) reviewStatsCache.set(key, { data: cached.data, fetchedAt: cached.fetchedAt });
    else reviewStatsCache.delete(key);
    throw error;
  }
}

export function fetchReviewAgents({ getAccessTokenSilently, logout }) {
  return new Promise((resolve, reject) => {
    getAccessTokenSilently()
      .then((token) => {
        const config = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        };

        plumeAPI
          .get("/critique_api/review-agents", config)
          .then((response) => {
            if (response.status === 200) {
              // Group by canonical tier, not the raw string: a whitelist of
              // literal names dropped every agent whose tier it didn't know,
              // which silenced the whole roster for newly-seeded users.
              const groupedAgents = response.data.reduce((acc, agent) => {
                const tier = canonicalTier(agent.tier);
                if (!acc[tier]) acc[tier] = [];
                acc[tier].push(agent);
                return acc;
              }, {});

              const sortedAgents = orderedTiers
                .filter((tier) => groupedAgents[tier])
                .map((tier) => ({
                  name: tier,
                  agents: groupedAgents[tier],
                }));
              resolve(sortedAgents);
            } else {
              console.error("Error fetching review agents:", response.statusText);
              reject(new Error(`Error fetching review agents: ${response.statusText}`));
            }
          })
          .catch((error) => {
            console.error("Error fetching review agents:", error);
            reject(error);
          });
      })
      .catch((error) => {
        console.error("Could not authenticate", error);
        logoutOnAuthError(error, logout);
        reject(error);
      });
  });
}

export function createReviewAgent({ getAccessTokenSilently, logout, agentData }) {
  return new Promise((resolve, reject) => {
    getAccessTokenSilently()
      .then((token) => {
        const config = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        };
        const processedData = {
          ...agentData,
          examples: JSON.stringify(agentData.examples),
        };
        plumeAPI
          .post("/critique_api/review-agents", processedData, config)
          .then((response) => {
            if (response.status === 201) {
              resolve(response.data);
            } else {
              // should never hit here under axios defaults, but just in case
              reject(new Error(`Unexpected status: ${response.status}`));
            }
          })
          .catch((error) => {
            console.error("Error creating review agent:", error);
            const msg = error.response && error.response.data && error.response.data.message ? error.response.data.message : error.message;
            reject(new Error(msg));
          });
      })
      .catch((error) => {
        console.error("Could not authenticate", error);
        logoutOnAuthError(error, logout);
        reject(error);
      });
  });
}

export function updateReviewAgent({ getAccessTokenSilently, logout, agentId, agentData }) {
  return new Promise((resolve, reject) => {
    getAccessTokenSilently()
      .then((token) => {
        const config = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        };
        const processedData = {
          ...agentData,
          examples: JSON.stringify(agentData.examples),
        };
        plumeAPI
          .put(`/critique_api/review-agents/${agentId}`, processedData, config)
          .then((response) => {
            if (response.status === 200) {
              resolve(response.data);
            } else {
              reject(new Error(`Unexpected status: ${response.status}`));
            }
          })
          .catch((error) => {
            console.error("Error updating review agent:", error);
            const msg = error.response && error.response.data && error.response.data.message ? error.response.data.message : error.message;
            reject(new Error(msg));
          });
      })
      .catch((error) => {
        console.error("Could not authenticate", error);
        logoutOnAuthError(error, logout);
        reject(error);
      });
  });
}

export function deleteReviewAgent({ getAccessTokenSilently, logout, agentId }) {
  return new Promise((resolve, reject) => {
    getAccessTokenSilently()
      .then((token) => {
        const config = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        };

        plumeAPI
          .delete(`/critique_api/review-agents/${agentId}`, config)
          .then((response) => {
            if (response.status === 200) {
              resolve(response.data);
            } else {
              console.error("Error deleting review agent:", response.statusText);
              reject(new Error(`Error deleting review agent: ${response.statusText}`));
            }
          })
          .catch((error) => {
            console.error("Error deleting review agent:", error);
            reject(error);
          });
      })
      .catch((error) => {
        console.error("Could not authenticate", error);
        logoutOnAuthError(error, logout);
        reject(error);
      });
  });
}

export function toggleReviewAgent({ getAccessTokenSilently, logout, agentId, isEnabled }) {
  return new Promise((resolve, reject) => {
    getAccessTokenSilently()
      .then((token) => {
        const config = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        };

        plumeAPI
          .patch(`/critique_api/review-agents/${agentId}/toggle`, { is_enabled: isEnabled }, config)
          .then((response) => {
            if (response.status === 200) {
              resolve(response.data);
            } else {
              console.error("Error toggling review agent:", response.statusText);
              reject(new Error(`Error toggling review agent: ${response.statusText}`));
            }
          })
          .catch((error) => {
            console.error("Error toggling review agent:", error);
            reject(error);
          });
      })
      .catch((error) => {
        console.error("Could not authenticate", error);
        logoutOnAuthError(error, logout);
        reject(error);
      });
  });
}

export function toggleTierAgents({ getAccessTokenSilently, logout, tierName, isEnabled }) {
  return new Promise((resolve, reject) => {
    getAccessTokenSilently()
      .then((token) => {
        const config = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        };

        plumeAPI
          .patch(`/critique_api/review-agents/tier/${tierName}/toggle`, { is_enabled: isEnabled }, config)
          .then((response) => {
            if (response.status === 200) {
              resolve(response.data);
            } else {
              console.error("Error toggling tier agents:", response.statusText);
              reject(new Error(`Error toggling tier agents: ${response.statusText}`));
            }
          })
          .catch((error) => {
            console.error("Error toggling tier agents:", error);
            reject(error);
          });
      })
      .catch((error) => {
        console.error("Could not authenticate", error);
        logoutOnAuthError(error, logout);
        reject(error);
      });
  });
}

export function updateSuggestionMetrics({ getAccessTokenSilently, logout, chatId, actionType }) {
  if (!chatId || !actionType) {
    console.error("Missing required parameters");
    return;
  }
  return new Promise((resolve, reject) => {
    getAccessTokenSilently()
      .then((token) => {
        const config = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        };

        plumeAPI
          .post(`/critique_api/chats/suggestion-metrics/${chatId}`, { action_type: actionType }, config)
          .then(function (response) {
            resolve(response.data);
          })
          .catch(function (error) {
            console.error("Error updating suggestion metrics:", error);
            reject(error);
          });
      })
      .catch((error) => {
        console.error("Could not authenticate", error);
        logoutOnAuthError(error, logout);
        reject(error);
      });
  });
}

export function uploadImage({ getAccessTokenSilently, file, bookId, chapterId, plumeAPI }) {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error("No file provided"));
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("book_id", bookId);
    formData.append("chapter_id", chapterId);

    getAccessTokenSilently()
      .then((token) => {
        const config = {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        };

        return plumeAPI.post("/images_api/images/upload", formData, config);
      })
      .then((response) => {
        if (response.data && response.data.url) {
          resolve(response.data.url);
        } else {
          throw new Error("No URL received in response");
        }
      })
      .catch((error) => {
        console.error("Error uploading image:", error);
        reject(error);
      });
  });
}

// ─── Referral program ───────────────────────────────────────────────────────

async function referralAuthConfig(getAccessTokenSilently, logout) {
  const token = await getAccessTokenSilently().catch((error) => {
    console.error("Could not authenticate", error);
    logoutOnAuthError(error, logout);
    throw error;
  });
  return { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } };
}

export async function getReferralLink({ getAccessTokenSilently, logout, plumeAPI }) {
  const config = await referralAuthConfig(getAccessTokenSilently, logout);
  const resp = await plumeAPI.get("/users_api/users/referral_link", config);
  return resp.data.referral_link;
}

export async function fetchReferrals({ getAccessTokenSilently, logout, plumeAPI }) {
  const config = await referralAuthConfig(getAccessTokenSilently, logout);
  const resp = await plumeAPI.get("/users_api/users/referrals", config);
  return resp.data;
}

export async function sendReferralInvitation({ getAccessTokenSilently, logout, plumeAPI, email, customMessage }) {
  const config = await referralAuthConfig(getAccessTokenSilently, logout);
  const payload = { email };
  if (customMessage) payload.custom_message = customMessage;
  const resp = await plumeAPI.post("/users_api/users/referral_invitation", payload, config);
  return resp.data;
}
